import React, { useState, useMemo } from "react";
import { Box, Button, Grid, Typography, Divider, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

import { getIconFromDriverName } from "./iconFunctions";
import ChatMetricsDriverForm from "./ChatMetricsDriverForm";
import ChatInterviewModal from "./ChatInterviewModal";
import { formatString } from "./valueFunctions";
import { getMetrics, getIntroText, getPurpose, getBasePayload, getSavedEntry, nestDotPaths } from "./metricsConfigFunctions";

const multiTypeDriver = [
    'litigation_risk',
    'regulatory_risk',
    'time_to_market',
    'incremental_revenue',
    'facilities_and_infrastructure',
    'production_lines_and_equipment',
    'tools_and_equipment',
    'inventory'
]

export function extractValues(obj) {
  if (!obj || typeof obj !== "object") return {};

  return Object.fromEntries(
    Object.entries(obj).map(([key, entry]) => [key, entry?.value ?? null])
  );
}

export default function OutcomeDriverSingle({
    valueModel,
    driver_analysis,
    selectedOutcome,
    selectedOutcomeExplanation,
    selectedDriver,
    selectedDriverReason,
    driverConfig,
    affectedTypes = [],
    onAddDriver,
}) {
    const theme = useTheme();

    const [modalOpen, setModalOpen] = useState(false);
    const [modalParams, setModalParams] = useState({
        mode: "metrics",
        category: "",
        subjectType: "",
        driver: "",
    });

    const grouped = React.useMemo(() => {
        if (!Array.isArray(affectedTypes) || affectedTypes.length === 0) return [];
        const byCat = affectedTypes.reduce((acc, { category, type }) => {
            (acc[category] ||= []).push(type);
            return acc;
        }, {});
        return Object.entries(byCat); // [ [category, [types...]], ... ]
    }, [affectedTypes]);

    const handleAddClick = ({ mode, category, subjectType, driver }) => {
        setModalParams({ mode, category, subjectType, driver });
        setModalOpen(true);
    };

    const costStructure = useSelector(
        (state) => state.value.costStructure
    );

    const industry_description = costStructure.description;

    // Keep your existing "saved" logic for the single-button case
    // const getSavedEntry = (driver) => {
    //     const targetArray =
    //         selectedDriver === driver
    //             ? valueModel?.risk_value_driver?.revenue_loss || []
    //             : [];
    //     return targetArray.length > 0;
    // };
    const saved = getSavedEntry(selectedDriver, selectedOutcome, modalParams.category, modalParams.selectedType, valueModel, affectedTypes);

    // const selectedMetrics = metricsConfig[selectedDriver].has_revenue_model ? metricsConfig[selectedDriver].metrics[driver_analysis.revenue_model] : metricsConfig[selectedDriver].metrics;

    const selectedMetrics = getMetrics(selectedDriver, driver_analysis.revenue_model, costStructure, valueModel.product, modalParams.category, modalParams.subjectType)

    return (
        <Box sx={{ maxWidth: 800 }}>
            <Box
                sx={{
                    mb: 3,
                    p: 2,
                    bgcolor: theme.palette.grey[200],
                    borderRadius: 1,
                }}
            >
                <Typography variant="body2">{getIntroText(selectedDriver)}</Typography>
            </Box>

            {Array.isArray(affectedTypes) && affectedTypes.length > 0 ? (
                // Render list of affected types with their own Add Driver buttons
                <React.Fragment>
                    {grouped.map(([category, types]) => (
                        <Box key={category} mb={4}>
                            <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                                {formatString(category)}
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            {types.map((type) => {
                                const saved = typeof getSavedEntry === "function"
                                    ? getSavedEntry(selectedDriver, selectedOutcome, category, type, valueModel, affectedTypes)
                                    : false;

                                return (
                                    <Grid
                                        container
                                        alignItems="center"
                                        columnSpacing={2}
                                        key={type}
                                        sx={{ mb: 3 }}   // extra space between rows
                                    >
                                        <Grid item xs={3}>
                                            <Typography>{formatString(type)}</Typography>
                                        </Grid>

                                        <Grid item xs={4} sx={{ textAlign: "right" }}>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                sx={{ textTransform: "none" }}
                                                startIcon={<Box component={getIconFromDriverName(selectedDriver)} />}
                                                onClick={() =>
                                                    handleAddClick({
                                                        mode: "metrics",
                                                        category,
                                                        subjectType: type,
                                                        driver: selectedDriver,
                                                    })
                                                }
                                                disabled={Boolean(saved)}
                                            >
                                                {saved ? "Saved" : "Add Driver"}
                                            </Button>
                                        </Grid>

                                        <Grid item xs={5} />
                                    </Grid>
                                );
                            })}
                        </Box>
                    ))}
                </React.Fragment>
            ) : (
                // Fallback to your original single Add Driver button
                <Button
                    variant="contained"
                    size="small"
                    sx={{ textTransform: "none" }}
                    startIcon={<Box component={getIconFromDriverName(selectedDriver)} />}
                    onClick={() =>
                        handleAddClick({
                            mode: "metrics",
                            category: "",
                            subjectType: "",
                            driver: selectedDriver,
                        })
                    }
                    disabled={Boolean(saved)}
                >
                    {saved ? "Saved" : "Add Driver"}
                </Button>
            )}

            <ChatInterviewModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                industry={industry_description}
                outcome={selectedOutcomeExplanation}
                purpose={getPurpose(selectedDriver)}
                reason={selectedDriverReason}
                driver={selectedDriver}
                metrics={selectedMetrics}
                extra_context={multiTypeDriver.includes(selectedDriver) ? `Category: ${modalParams.category} Type: ${modalParams.subjectType}` : ""}
                mode={modalParams.mode || "metrics"}
                FormComponent={ChatMetricsDriverForm}
                formProps={{
                    metrics: selectedMetrics,
                    onSubmit: (payload, { setSubmitting, setErrors }) => {
                        setSubmitting(true);

                        let payloadValues = extractValues(payload)
                        let nestedPayloadValues = nestDotPaths(payloadValues)
                        let {type, basePayload} = getBasePayload(selectedDriver, driver_analysis.revenue_model, true, selectedOutcome, modalParams.category, modalParams.subjectType, costStructure, '€');

                        onAddDriver(type, {...basePayload, ...nestedPayloadValues}, { setSubmitting, setErrors });
                        setSubmitting(false);
                        setModalOpen(false);
                    },
                }}
            />
        </Box>
    );
}

OutcomeDriverSingle.propTypes = {
    valueModel: PropTypes.object.isRequired,
    driver_analysis: PropTypes.object.isRequired,
    selectedOutcome: PropTypes.string.isRequired,
    selectedOutcomeExplanation: PropTypes.string.isRequired,
    selectedDriver: PropTypes.string.isRequired,
    selectedDriverReason: PropTypes.string,
    driverConfig: PropTypes.object.isRequired,
    affectedTypes: PropTypes.arrayOf(
        PropTypes.shape({
            category: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
        })
    ),
    onAddDriver: PropTypes.func,
};